// ---------------------------------------------------------------------------
// Sampling the opponent's Grid — Grids consistent with what a player knows
// ---------------------------------------------------------------------------
//
// sampleOpponentGrid(oppGrid, oppLost, rng) → a full homefront the opponent
// could be holding, given only the view (bots/view.js):
//
//   - every structure the view shows whole (a hit one) stands exactly there,
//     dead or damaged as the craters say
//   - every other known structure cell (a scanned one, a rocket's) is covered
//     by a structure of a maker shape, all of its cells intact
//   - known open land and plain craters hold nothing; unseen cells may
//   - the Grid fields homefrontMaxStructures structures in all (the dead ones
//     included: the opponent's kill count says how many are dead)
//   - intel stamps: no LIVE structure cell sits closer to a stamp than its
//     distance (every live cell now was live when it was stamped), and a
//     stamp whose structure still stands has a structure cell at exactly its
//     distance
//   - visible rockets sit where they are seen; broods are copied as seen
//   - no structure on a cell that has ever held a brood (pastBroodCells):
//     broods can't be built on structures, and structures never move
//
// Structures are placed constructively (random maker shapes on cells that can
// still take them, stamp rings first), then the stamps are checked; a sample
// that breaks one is redrawn, up to `tries`, and the best one found is kept
// (fewest broken stamps). Shape limits per Grid are not enforced — a player
// only sees the shapes of the structures it has hit.

import { rules } from '../config.js'
import { makeHomefront, placeStructure, isStructureDestroyed } from '../subplotGrid.js'
import { hexDistance } from '../hexGrid.js'
import { STRUCTURE_SHAPES, SHAPE_OFFSETS, shapeFootprint } from '../homefrontMaker.js'
import { addBrood, matureBroodAt } from '../subplotBases.js'
import { pick, shuffled } from './rng.js'

const SHAPES = STRUCTURE_SHAPES.map(s => s.key)

// Precomputed footprints of every shape at every tap, for the random fill.
let ALL = null
function allFootprints() {
    if (ALL) return ALL
    ALL = []
    for (const shape of SHAPES) for (let tap = 0; tap < 315; tap++) {
        const fp = shapeFootprint(shape, tap)
        if (fp) ALL.push(fp)
    }
    return ALL
}
const COVERING = new Map()
function footprintsThrough(s) {
    if (!COVERING.has(s)) COVERING.set(s, allFootprints().filter(fp => fp.includes(s)))
    return COVERING.get(s)
}

function stampViolations(hf, stamps) {
    const live = [], all = []
    for (const [id, st] of Object.entries(hf.structures)) {
        for (const s of st.footprint) {
            all.push(s)
            if (!hf.subplots[s].destroyed) live.push(s)
        }
        void id
    }
    let bad = 0
    for (const t of stamps) {
        if (t.distance == null) continue
        if (live.some(s => hexDistance(s, t.s) < t.distance)) bad++
        else if (!t.dead && !all.some(s => hexDistance(s, t.s) === t.distance)) bad++
    }
    return bad
}

function buildOnce(grid, oppLost, rng) {
    const n = grid.cells.length
    const hf = makeHomefront(() => null)
    // What the player sees of each cell.
    grid.cells.forEach((c, s) => {
        const cell = hf.subplots[s]
        if (c.destroyed) { cell.destroyed = true; cell.hp = 0; cell.revealed = true }
        if (c.scanned) { cell.scanned = 'aged'; cell.revealed = true }
    })
    for (const t of grid.stamps) {
        const cell = hf.subplots[t.s]
        cell.distance = t.distance; cell.distanceLetter = t.letter; cell.distanceDead = t.dead
    }
    const taken = new Set()
    // A live structure may not sit within a stamp's radius.
    const forbiddenLive = new Set()
    for (const t of grid.stamps) {
        if (t.distance == null) continue
        for (let s = 0; s < n; s++) if (hexDistance(s, t.s) < t.distance) forbiddenLive.add(s)
    }
    const brood = new Set([...grid.broods.flatMap(b => b.footprint), ...(grid.pastBroodCells ?? [])])
    const knownStruct = (s) => grid.cells[s].known === 'structure' && !brood.has(s)
    let count = 0
    const place = (fp) => { placeStructure(hf, `S${++count}`, fp); fp.forEach(s => taken.add(s)) }

    // 1. Structures seen whole.
    for (const g of grid.groups) place(g.cells)
    // 2. Other known structure cells: an intact structure over each.
    const freeForIntact = (s) => !taken.has(s) && !brood.has(s) && !grid.cells[s].destroyed &&
        grid.cells[s].known !== 'empty' && !forbiddenLive.has(s)
    for (let s = 0; s < n; s++) {
        if (!knownStruct(s) || taken.has(s)) continue
        const opts = footprintsThrough(s).filter(fp => fp.every(freeForIntact))
        // A known cell inside a stamp's radius can't be live — only possible
        // for a destroyed cell, which the groups already hold; fall back to a
        // single cell ignoring the radius so the known cell is still covered.
        place(opts.length ? pick(rng, opts) : [s])
    }
    const total = rules().homefrontMaxStructures
    // 3. Stamps whose structure stands need a structure cell at their distance.
    for (const t of shuffled(rng, grid.stamps)) {
        if (count >= total || t.distance == null || t.dead) continue
        const has = [...taken].some(s => hexDistance(s, t.s) === t.distance)
        if (has) continue
        const ring = []
        for (let s = 0; s < n; s++) if (hexDistance(s, t.s) === t.distance && freeForIntact(s)) ring.push(s)
        for (const s of shuffled(rng, ring)) {
            const opts = footprintsThrough(s).filter(fp => fp.every(freeForIntact))
            if (opts.length) { place(pick(rng, opts)); break }
        }
    }
    // 4. Fill to the Grid's structure count (dead ones are already among the
    //    groups: the player saw every kill land).
    for (let tries = 0; count < total && tries < 400; tries++) {
        const fp = pick(rng, allFootprints())
        if (fp.every(freeForIntact)) place(fp)
    }
    // Rockets where they are seen.
    for (const s of grid.rockets) {
        const id = hf.subplots[s].structureId
        if (id == null) continue
        const st = hf.structures[id]
        st.rockets = (st.rockets || 0) + 1
        st.rocketCells = [...(st.rocketCells || []), s]
    }
    // Broods as seen.
    for (const b of grid.broods) {
        const st = addBrood(hf, b.col, b.centerS, 0) ?? forceBrood(hf, b)
        if (!b.underConstruction) {
            matureBroodAt(hf, b.col, b.centerS)
            if (b.egg) st.egg = b.egg
        }
        if (b.spent) st.spent = true
    }
    void oppLost
    return hf
}

// A brood the placement check refuses (a cell cratered since it was built):
// stamped anyway, as seen (over any sampled structure's cell).
function forceBrood(hf, b) {
    const id = `base-c${b.col}-s${b.centerS}-seen`
    for (const s of b.footprint) { hf.subplots[s].structureId = null; hf.subplots[s].broodId = id }
    const st = { id, footprint: [...b.footprint], corridor: b.col, underConstruction: true }
    hf.broods[id] = st
    return st
}

export function sampleOpponentGrid(grid, oppLost, rng, { tries = 12 } = {}) {
    let best = null, bestBad = Infinity
    for (let i = 0; i < tries; i++) {
        const hf = buildOnce(grid, oppLost, rng)
        const bad = stampViolations(hf, grid.stamps)
        if (bad < bestBad) { best = hf; bestBad = bad }
        if (bad === 0) break
    }
    return best
}

// How often each cell holds a live structure across samples: the search
// bot's target map.
export function structureOdds(samples) {
    const odds = new Float64Array(samples[0]?.subplots.length ?? 0)
    for (const hf of samples) {
        hf.subplots.forEach((c, s) => {
            if (c.structureId == null || c.destroyed) return
            if (!isStructureDestroyed(hf, c.structureId)) odds[s] += 1 / samples.length
        })
    }
    return odds
}

